<script setup lang="ts">
/**DataTableFacetedFilter */
import type { Column } from '@tanstack/vue-table'
import type { Component } from 'vue'
import type { Campaign } from './schema'
import { computed } from 'vue'


// Usando os componentes shadcn que você baixou
import { Badge } from 'dashboard/components-shadcn/new-york/ui/badge'
import { Button } from 'dashboard/components-shadcn/new-york/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from 'dashboard/components-shadcn/new-york/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'dashboard/components-shadcn/new-york/ui/popover'
import { Separator } from 'dashboard/components-shadcn/new-york/ui/separator'

interface DataTableFacetedFilter {
  column?: Column<Campaign, any>
  title?: string
  options: {
    label: string
    value: string
    icon?: Component
  }[]
  disabled?: boolean
}

const props = withDefaults(defineProps<DataTableFacetedFilter>(), {
  disabled: false,
})

const facets = computed(() => props.column?.getFacetedUniqueValues())
const selectedValues = computed(() => new Set(props.column?.getFilterValue() as string[]))

// Função para classes condicionais (substitui cn)
const classNames = (...classes: (string | undefined | boolean)[]) => {
  return classes.filter(Boolean).join(' ')
}
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button
        variant="primary"
        size="sm"
        :disabled="disabled"
        :class="[
          'h-8 border-b border-n-slate-6 bg-n-slate-6 hover:bg-n-slate-4 dark:hover:bg-slate-4 transition-colors',
          { 'opacity-50 cursor-not-allowed': disabled }
        ]"
      >
        <span class="i-lucide-plus-circle mr-2 h-4 w-4 text-n-slate-11"></span>
        <span class="text-n-slate-12">{{ title }}</span>
        <template v-if="selectedValues.size > 0">
          <Separator orientation="vertical" class="mx-2 h-4 bg-n-slate-6" />
          <Badge
            variant="secondary"
            class="rounded-sm px-1 font-normal lg:hidden bg-green-100 text-green-800 border-green-200"
          >
            {{ selectedValues.size }}
          </Badge>
          <div class="hidden space-x-1 lg:flex">
            <Badge
              v-if="selectedValues.size > 2"
              variant="secondary"
              class="rounded-sm px-1 font-normal bg-green-100 text-green-800 border-green-200"
            >
              {{ selectedValues.size }} selecionado(s)
            </Badge>

            <template v-else>
              <Badge
                v-for="option in options
                  .filter((option) => selectedValues.has(option.value))"
                :key="option.value"
                variant="secondary"
                class="rounded-sm px-1 font-normal bg-green-100 text-green-800 border-green-200"
              >
                {{ option.label }}
              </Badge>
            </template>
          </div>
        </template>
      </Button>
    </PopoverTrigger>

    <PopoverContent
      class="w-[220px] p-0 bg-n-slate-1 border-n-slate-6 shadow-lg rounded-lg overflow-hidden"
      align="start"
    >
      <div class="bg-n-slate-1">
        <Command class="bg-n-slate-1">

          <CommandList class="bg-n-slate-1 max-h-60 overflow-y-auto">

            <CommandGroup class="p-1 bg-n-slate-1">
              <CommandItem
                v-for="option in options"
                :key="option.value"
                :value="option"
                class="flex items-center px-2 py-2 text-sm bg-n-slate-1 hover:bg-n-slate-3 rounded-sm cursor-pointer transition-colors"
                @select="(e) => {
                  const currentValues = new Set(column?.getFilterValue() as string[] || [])
                  const isSelected = currentValues.has(option.value)
                  if (isSelected) {
                    currentValues.delete(option.value)
                  }
                  else {
                    currentValues.add(option.value)
                  }
                  const filterValues = Array.from(currentValues)
                  column?.setFilterValue(
                    filterValues.length ? filterValues : undefined,
                  )
                }"
              >
                <div
                  :class="classNames(
                    'mr-3 flex h-4 w-4 items-center justify-center rounded border transition-colors',
                    selectedValues.has(option.value)
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'border-n-slate-7 hover:border-n-slate-8',
                  )"
                >
                  <span
                    v-if="selectedValues.has(option.value)"
                    class="i-lucide-check h-3 w-3"
                  ></span>
                </div>

                <component
                  :is="option.icon"
                  v-if="option.icon"
                  class="mr-2 h-4 w-4 text-n-slate-10"
                />

                <span class="flex-1 text-n-slate-12">{{ option.label }}</span>

                <span
                  v-if="facets?.get(option.value)"
                  class="ml-auto text-xs text-n-slate-10 bg-n-slate-3 px-1.5 py-0.5 rounded font-mono"
                >
                  {{ facets.get(option.value) }}
                </span>
              </CommandItem>
            </CommandGroup>

            <template v-if="selectedValues.size > 0">
              <div class="border-t border-n-slate-4">
                <CommandGroup class="p-1 bg-n-slate-1">
                  <CommandItem
                    :value="{ label: 'Limpar filtros' }"
                    class="flex items-center justify-center px-2 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer transition-colors bg-n-slate-1"
                    @select="column?.setFilterValue(undefined)"
                  >
                    <span class="i-lucide-x mr-2 h-3 w-3"></span>
                    Limpar filtros
                  </CommandItem>
                </CommandGroup>
              </div>
            </template>
          </CommandList>
        </Command>
      </div>
    </PopoverContent>
  </Popover>
</template>


